import { API } from '../api.js';
import { useState, useRef } from 'react';
import { useAuth }  from '../contexts/AuthContext';
import { useLang }  from '../contexts/LangContext';
import styles from './ProfileTab.module.css';

export default function ProfileTab({ onToast }) {
  const { user, token, refreshUser } = useAuth();
  const { t } = useLang();
  const fileRef = useRef();

  const [form, setForm] = useState({ display_name: user?.display_name || '', bio: user?.bio || '' });
  const [pwd,  setPwd]  = useState({ current: '', newPwd: '', confirm: '' });
  const [saving, setSaving]   = useState(false);
  const [pwdSaving, setPwdSaving] = useState(false);
  const [error, setError] = useState('');
  const [pwdError, setPwdError] = useState('');

  const planName = user?.plan_name || (user?.role === 'admin' ? 'Administrator' : 'Free');
  const planColor = user?.plan_color || (user?.role === 'admin' ? '#ff6b6b' : '#4a5280');

  async function saveProfile(e) {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      const res = await fetch(`${API}/api/users/me`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error((await res.json()).error);
      await refreshUser();
      onToast(t('profile.saved'));
    } catch (err) { setError(err.message); }
    setSaving(false);
  }

  async function changePassword(e) {
    e.preventDefault();
    setPwdError('');
    if (pwd.newPwd.length < 8) { setPwdError(t('auth.weakPassword')); return; }
    if (pwd.newPwd !== pwd.confirm) { setPwdError(t('auth.passwordMismatch')); return; }
    setPwdSaving(true);
    try {
      const res = await fetch(`${API}/api/auth/change-password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ current_password: pwd.current, new_password: pwd.newPwd }),
      });
      if (!res.ok) throw new Error((await res.json()).error);
      setPwd({ current: '', newPwd: '', confirm: '' });
      onToast(t('profile.passwordUpdated'));
    } catch (err) { setPwdError(err.message); }
    setPwdSaving(false);
  }

  async function handleAvatarChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('avatar', file);
    const res = await fetch(`${API}/api/users/me/avatar`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: fd,
    });
    if (res.ok) { await refreshUser(); onToast(t('common.success')); }
  }

  return (
    <div className={styles.wrapper}>
      {/* Left: Avatar + Plan */}
      <div className={styles.left}>
        <div className={styles.avatarSection}>
          <div className={styles.avatarWrap} onClick={() => user?.role !== 'admin' && fileRef.current.click()}>
            {user?.avatar_url
              ? <img src={`${API}${user.avatar_url}`} alt="avatar" className={styles.avatarImg} />
              : <div className={styles.avatarPlaceholder}>{(user?.display_name?.[0] || '?').toUpperCase()}</div>
            }
            {user?.role !== 'admin' && <div className={styles.avatarOverlay}>📷</div>}
          </div>
          {user?.role !== 'admin' && (
            <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAvatarChange} />
          )}
          <div className={styles.avatarName}>{user?.display_name}</div>
          <div className={styles.avatarEmail}>{user?.email}</div>
          <div className={styles.planBadge} style={{ background: `${planColor}22`, color: planColor, border: `1px solid ${planColor}44` }}>
            {planName}
          </div>
        </div>

        <div className="card">
          <div className="card-header"><div className="card-title">📊 Stats</div></div>
          <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[
              ['⚡ XP', user?.xp?.toLocaleString() ?? '0'],
              ['🏆 Level', `Lv.${user?.level ?? 1}`],
              ['🔥 Streak', `${user?.streak ?? 0} days`],
            ].map(([label, val]) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                <span style={{ color: 'var(--text2)' }}>{label}</span>
                <span style={{ fontWeight: 700, color: 'var(--text)' }}>{val}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right: Forms */}
      <div className={styles.right}>
        {/* Profile form */}
        <div className="card">
          <div className="card-header"><div className="card-title">👤 {t('profile.title')}</div></div>
          <div className="card-body">
            {error && <div className={styles.error}>{error}</div>}
            <form onSubmit={saveProfile}>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">{t('profile.displayName')}</label>
                  <input className="form-input" value={form.display_name} onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">{t('profile.email')}</label>
                  <input className="form-input" value={user?.email || ''} disabled style={{ opacity: 0.5 }} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">{t('profile.bio')}</label>
                <textarea className="form-input" value={form.bio} onChange={e => setForm(f => ({ ...f, bio: e.target.value }))} placeholder="Tell us about yourself…" />
              </div>
              <button type="submit" className="btn btn-primary" disabled={saving || user?.role === 'admin'}>
                {saving ? t('common.loading') : t('profile.saveChanges')}
              </button>
              {user?.role === 'admin' && <span style={{ marginLeft: 12, fontSize: 12, color: 'var(--text3)' }}>Admin profile is managed via config.ini</span>}
            </form>
          </div>
        </div>

        {/* Change password */}
        {user?.role !== 'admin' && (
          <div className="card">
            <div className="card-header"><div className="card-title">🔒 {t('profile.changePassword')}</div></div>
            <div className="card-body">
              {pwdError && <div className={styles.error}>{pwdError}</div>}
              <form onSubmit={changePassword}>
                <div className="form-group">
                  <label className="form-label">{t('profile.currentPassword')}</label>
                  <input type="password" className="form-input" value={pwd.current} onChange={e => setPwd(p => ({ ...p, current: e.target.value }))} placeholder="••••••••" />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">{t('profile.newPassword')}</label>
                    <input type="password" className="form-input" value={pwd.newPwd} onChange={e => setPwd(p => ({ ...p, newPwd: e.target.value }))} placeholder="••••••••" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('profile.confirmNew')}</label>
                    <input type="password" className="form-input" value={pwd.confirm} onChange={e => setPwd(p => ({ ...p, confirm: e.target.value }))} placeholder="••••••••" />
                  </div>
                </div>
                <button type="submit" className="btn btn-primary" disabled={pwdSaving}>
                  {pwdSaving ? t('common.loading') : t('profile.updatePassword')}
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
